/**
 * Racing AI.
 *
 * The AI drives the *same* physics as the player and reads the same surface
 * grid — difficulty comes from how well it plans, how early it brakes, how
 * consistent it is and how often it makes mistakes, never from a speed
 * multiplier.
 *
 * Control is pure-pursuit steering plus a braking-distance target-speed solve
 * over the built racing line, with recovery, overtaking and defensive
 * behaviours layered on top.
 */

import { clamp, makeRng } from './math.js';
import type { CarHandling } from './cars.js';
import { SURFACES } from './surfaces.js';
import { nearestPathIndexNear, surfaceAt, type BuiltTrack, type RacingPoint } from './track.js';
import { surfaceGrip, type VehicleInput, type VehicleState } from './vehicle.js';
import { CAR_GRAVITY } from './cars.js';

export type AIDifficulty = 'beginner' | 'easy' | 'normal' | 'hard' | 'expert' | 'adaptive';

export interface AITraits {
  id: AIDifficulty;
  name: string;
  blurb: string;
  /** 0..1 — sets corner speed, braking points and throttle precision. */
  skill: number;
  /** 0..1 — how close they dare to run and how hard they defend. */
  aggression: number;
  /** 0..1 — how repeatable their lap times are. */
  consistency: number;
  /** 0..1 — steering reaction / how quickly they notice changes. */
  reaction: number;
  /** 0..1 — willingness to take the risky line. */
  risk: number;
  /** Mistakes per minute. */
  mistakeRate: number;
  /** Multiplier applied to the computed safe corner speed. */
  cornerSpeed: number;
  /** 1 = brakes at the last moment, <1 = brakes earlier. */
  brakeAggression: number;
  /** Look-ahead distance in px for the target-speed solve. */
  lookahead: number;
  /** How much of the available downforce/aero they exploit. */
  aeroUse: number;
}

export const AI_DIFFICULTIES: Record<AIDifficulty, AITraits> = {
  beginner: {
    id: 'beginner', name: 'Beginner',
    blurb: 'Drives cautiously, brakes very early and gets caught out by corners.',
    skill: 0.34, aggression: 0.15, consistency: 0.35, reaction: 0.35, risk: 0.1,
    mistakeRate: 7, cornerSpeed: 0.72, brakeAggression: 0.72, lookahead: 150, aeroUse: 0.3,
  },
  easy: {
    id: 'easy', name: 'Easy',
    blurb: 'Follows the racing line but leaves the door open and makes errors.',
    skill: 0.5, aggression: 0.3, consistency: 0.5, reaction: 0.5, risk: 0.2,
    mistakeRate: 4, cornerSpeed: 0.81, brakeAggression: 0.82, lookahead: 170, aeroUse: 0.45,
  },
  normal: {
    id: 'normal', name: 'Normal',
    blurb: 'Consistent, sensible racing with basic defensive driving.',
    skill: 0.68, aggression: 0.5, consistency: 0.68, reaction: 0.65, risk: 0.35,
    mistakeRate: 2.2, cornerSpeed: 0.9, brakeAggression: 0.92, lookahead: 190, aeroUse: 0.6,
  },
  hard: {
    id: 'hard', name: 'Hard',
    blurb: 'Precise braking points, clean lines and strategic overtakes.',
    skill: 0.82, aggression: 0.66, consistency: 0.82, reaction: 0.8, risk: 0.5,
    mistakeRate: 1.1, cornerSpeed: 0.96, brakeAggression: 1.0, lookahead: 210, aeroUse: 0.8,
  },
  expert: {
    id: 'expert', name: 'Expert',
    blurb: 'Aggressive, relentless pace that uses every bit of available grip.',
    skill: 0.94, aggression: 0.85, consistency: 0.93, reaction: 0.92, risk: 0.7,
    mistakeRate: 0.45, cornerSpeed: 1.0, brakeAggression: 1.06, lookahead: 230, aeroUse: 1,
  },
  adaptive: {
    id: 'adaptive', name: 'Adaptive',
    blurb: 'Reads your recent results and dials its own pace to match — fairly.',
    skill: 0.7, aggression: 0.55, consistency: 0.75, reaction: 0.72, risk: 0.4,
    mistakeRate: 1.4, cornerSpeed: 0.92, brakeAggression: 0.97, lookahead: 200, aeroUse: 0.72,
  },
};

export const AI_ORDER: AIDifficulty[] = ['beginner', 'easy', 'normal', 'hard', 'expert', 'adaptive'];

export interface AIState {
  traits: AITraits;
  rng: () => number;
  /** Lateral bias 0 = centre, negative = left of the racing line. */
  laneBias: number;
  /** Remaining seconds of an injected mistake. */
  mistakeTimer: number;
  mistakeSteer: number;
  mistakeThrottle: number;
  /** Seconds spent stuck. */
  stuckTimer: number;
  /** Remaining seconds of the reverse-out-of-trouble manoeuvre. */
  reverseTimer: number;
  /** Rolling "is the car actually going anywhere?" window. */
  stallWindow: number;
  advanceInWindow: number;
  movedInWindow: number;
  lastX: number;
  lastY: number;
  /** Per-driver aggression/consistency jitter. */
  skillJitter: number;
  /** Set while the driver is deliberately defending. */
  defending: boolean;
  /** Last path index we were heading to. Used as the search hint, so recall it
   * before it is overwritten to measure real progress along the circuit. */
  lastIndex: number;
  /** Adaptive difficulty: current pace scale. */
  adapt: number;
  /** Optional lateral offset target for overtaking, in px. */
  overtakeOffset: number;
}

export function createAIState(difficulty: AIDifficulty, seed: number): AIState {
  const traits = AI_DIFFICULTIES[difficulty];
  const rng = makeRng(seed);
  return {
    traits,
    rng,
    laneBias: 0,
    mistakeTimer: 0,
    mistakeSteer: 0,
    mistakeThrottle: 0,
    stuckTimer: 0,
    reverseTimer: 0,
    stallWindow: 0,
    advanceInWindow: 0,
    movedInWindow: 0,
    lastX: 0,
    lastY: 0,
    skillJitter: 0.94 + rng() * 0.12,
    defending: false,
    lastIndex: 0,
    adapt: 1,
    overtakeOffset: 0,
  };
}

export interface AIObstacle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  speed: number;
}

export interface AIWorld {
  track: BuiltTrack;
  /** Reused pool of other cars; only the first `otherCount` entries are valid. */
  others: AIObstacle[];
  otherCount: number;
}

const NEUTRAL: VehicleInput = { throttle: 0, brake: 0, steer: 0, handbrake: false, boost: false };

/** How often the "is the car actually going anywhere?" check runs. */
const STALL_WINDOW = 2;
/** Net centreline samples / px of ground the driver must gain per window. */
const STALL_MIN_ADVANCE = 3;
const STALL_MIN_MOVE = 30;

/** Signed shortest distance from one centreline index to another. */
function wrappedIndexDelta(delta: number, length: number): number {
  if (length <= 0) return 0;
  let d = delta % length;
  if (d > length / 2) d -= length;
  if (d < -length / 2) d += length;
  return d;
}

/**
 * Produces this frame's control input for an AI driver. Allocates nothing on
 * the hot path (the output object is reused by the caller).
 */
export function updateAI(
  ai: AIState,
  state: VehicleState,
  handling: CarHandling,
  world: AIWorld,
  dt: number,
  out: VehicleInput,
): void {
  const traits = ai.traits;
  const track = world.track;
  const line = track.racingLine;
  if (line.length === 0) {
    Object.assign(out, NEUTRAL);
    return;
  }

  const speed = state.speed;
  // Constrained to the stretch the driver was on last frame: a global nearest
  // query would happily snap across a hairpin onto the other leg of the track
  // and send the car the wrong way.
  const previousIndex = ai.lastIndex;
  const idx = nearestPathIndexNear(track, state.x, state.y, previousIndex);
  ai.lastIndex = idx;
  const base = track.path[idx]!;

  // ---------------------------------------------------------- stuck recovery
  // Two ways to be stuck. The obvious one is standing still. The other is
  // grinding along a barrier: the position is clamped back to the same spot
  // every frame, so the car reports a healthy speed while making no progress at
  // all, and a speed-only test would leave it there for ever.
  ai.advanceInWindow += wrappedIndexDelta(idx - previousIndex, line.length);
  ai.movedInWindow += Math.hypot(state.x - ai.lastX, state.y - ai.lastY);
  ai.lastX = state.x;
  ai.lastY = state.y;
  ai.stallWindow += dt;
  let grinding = false;
  if (ai.stallWindow >= STALL_WINDOW) {
    grinding = ai.advanceInWindow < STALL_MIN_ADVANCE && ai.movedInWindow < STALL_MIN_MOVE;
    ai.stallWindow = 0;
    ai.advanceInWindow = 0;
    ai.movedInWindow = 0;
  }
  if (speed < 12 || grinding) ai.stuckTimer += dt;
  else ai.stuckTimer = Math.max(0, ai.stuckTimer - dt * 2);
  if (ai.reverseTimer > 0) {
    ai.reverseTimer -= dt;
    // Backing out of trouble: swing the nose back toward the circuit. Steering
    // yaws the car the same way whichever direction it is travelling, so the
    // same sign aims the nose forward and in reverse.
    const back = Math.atan2(base.y - state.y, base.x - state.x);
    const relBack = (((back - state.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
    out.throttle = 0;
    out.brake = 1;
    out.steer = clamp(relBack * 1.4, -1, 1);
    out.handbrake = false;
    out.boost = false;
    return;
  }
  if (ai.stuckTimer > 2.0) {
    ai.stuckTimer = 0;
    ai.reverseTimer = 0.9 + ai.rng() * 0.5;
    const back = Math.atan2(base.y - state.y, base.x - state.x);
    const relBack = (((back - state.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
    out.throttle = 0;
    out.brake = 1;
    out.steer = clamp(relBack * 1.4, -1, 1);
    out.handbrake = false;
    out.boost = false;
    return;
  }
  // ------------------------------------------------- injected human mistakes
  if (traits.consistency < 1 && ai.mistakeTimer <= 0) {
    const chance = (traits.mistakeRate / 60) * dt * (1.4 - traits.consistency);
    if (ai.rng() < chance) {
      ai.mistakeTimer = 0.4 + ai.rng() * 0.9;
      ai.mistakeSteer = (ai.rng() - 0.5) * 1.4;
      ai.mistakeThrottle = ai.rng() < 0.45 ? -1 : 0.2;
    }
  }
  if (ai.mistakeTimer > 0) ai.mistakeTimer -= dt;

  // ----------------------------------------------------------- overtaking
  ai.overtakeOffset *= 1 - Math.min(1, dt * 1.2);
  for (let oi = 0; oi < world.otherCount; oi++) {
    const o = world.others[oi]!;
    const dx = o.x - state.x;
    const dy = o.y - state.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 200) continue;
    const heading = Math.atan2(dy, dx);
    const rel = (((heading - state.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
    if (Math.abs(rel) > 0.55) continue;
    if (Math.hypot(o.vx, o.vy) < 20 && dist < 90) continue;
    // Steer around the obstacle, choosing the freer side.
    const side = rel > 0 ? 1 : -1;
    const want = -side * (36 + traits.aggression * 40);
    ai.overtakeOffset = clamp(ai.overtakeOffset + want * dt * 3, -70, 70);
    if (dist < 60 && traits.aggression < 0.4) ai.overtakeOffset *= 0.3;
  }

  // -------------------------------------------------------- defensive line
  ai.defending = traits.aggression > 0.55 && ai.overtakeOffset === 0 && speed > 60;

  // -------------------------------------------------- target speed solving
  const mu = handling.muBase * surfaceGrip(handling, state.surfaceRear);
  const cornerSpeedScale = traits.cornerSpeed * ai.skillJitter * ai.adapt;
  const driveGrip = clamp(mu, 0.3, 3.2);
  const decel = Math.max(24, (handling.brakeForce / handling.mass) * clamp(driveGrip, 0.3, 2.0) * traits.brakeAggression);
  // On ice or oil a car cannot slow down anywhere near as fast as it can on
  // tarmac, so the braking solve has to be planned much further ahead or the
  // driver simply arrives at the corner far too quickly.
  const gripLookahead = clamp(1 / driveGrip, 1, 3);

  let targetSpeed = handling.maxSpeed;
  const lookahead = traits.lookahead * (0.6 + clamp(speed / handling.maxSpeed, 0, 1) * 0.8) * gripLookahead;
  const spacing = track.path.length > 1 ? Math.max(4, (line[1]!.dist - line[0]!.dist) || 8) : 8;
  const steps = Math.min(line.length - 2, Math.ceil(lookahead / spacing));
  for (let k = 1; k <= steps; k++) {
    const p: RacingPoint = line[(idx + k) % line.length]!;
    const d = k * spacing;
    // Account for the surface the line passes over at that point: an ice patch
    // ahead is a corner as far as the tyres are concerned.
    const aheadGrip = clamp(Math.min(driveGrip, surfaceGrip(handling, surfaceAt(track, p.x, p.y))), 0.3, 3.2);
    const pointBudget = cornerBudget(p.radius, aheadGrip, cornerSpeedScale) * (1 - (1 - traits.aeroUse) * 0.06);
    const allowed = Math.sqrt(Math.max(0, pointBudget * pointBudget + 2 * decel * d));
    if (allowed < targetSpeed) targetSpeed = allowed;
  }
  targetSpeed = Math.min(targetSpeed, handling.maxSpeed);
  if (!state.onRoad) targetSpeed *= 0.72;

  // ------------------------------------------------- pure pursuit steering
  const pursue = clamp(26 + speed * 0.28, 40, 190);
  const lineIdx = (idx + Math.round(pursue / spacing)) % line.length;
  let targetX = line[lineIdx]!.x;
  let targetY = line[lineIdx]!.y;

  // Personality lateral offset relative to the path tangent.
  const lane = clamp(ai.laneBias + ai.overtakeOffset * 0.6, -60, 60);
  const nx = -Math.sin(base.angle);
  const ny = Math.cos(base.angle);
  targetX += nx * lane;
  targetY += ny * lane;

  const steerAngleToTarget = Math.atan2(targetY - state.y, targetX - state.x);
  const rel = (((steerAngleToTarget - state.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
  let steer = clamp(rel / Math.max(0.18, handling.steerMax * 1.05), -1, 1);
  // Counter-steer authority grows with how sideways the car is.
  steer -= clamp(state.vLat / 120, -0.5, 0.5);

  if (ai.mistakeTimer > 0) {
    steer = clamp(steer + ai.mistakeSteer, -1, 1);
  }

  // ------------------------------------------------------ throttle / brake
  const error = targetSpeed - speed;
  let throttle = 0;
  let brake = 0;
  if (ai.mistakeTimer > 0 && ai.mistakeThrottle < 0) {
    brake = clamp(0.4 + ai.rng() * 0.3, 0, 1);
    throttle = 0;
  } else if (error > 4) {
    throttle = clamp(error / 45, 0, 1);
    throttle = Math.pow(throttle, 0.7);
    throttle = clamp(throttle + (1 - traits.skill) * 0.05, 0, 1);
  } else if (error < -8) {
    brake = clamp(-error / 55, 0, 1);
    // Better drivers trail-brake instead of slamming the pedal.
    brake *= 0.55 + traits.skill * 0.45;
  } else {
    throttle = clamp(0.4 + traits.skill * 0.4, 0, 1);
  }

  // Slide management: lift off if the car is sideways.
  const slide = Math.atan2(Math.abs(state.vLat), Math.max(8, Math.abs(state.vLong)));
  if (slide > 0.4) {
    throttle *= clamp(1 - (slide - 0.4) * 1.4, 0.2, 1);
    if (slide > 0.75) brake = Math.max(brake, 0.2);
  }

  // ------------------------------------------------------------- handbrake
  const handbrake = false;
  if (traits.risk > 0.6 && Math.abs(base.curvature) > 0.022 && speed > 120) {
    // The brave ones add lock on a hairpin to rotate the car.
    steer = clamp(steer + 0.15, -1, 1);
  }

  // ------------------------------------------------------------- turbo use
  const straightAhead =
    line[(idx + 6) % line.length]!.radius > 900 && line[(idx + 12) % line.length]!.radius > 700;
  const boost = straightAhead && state.turboCharge > 0.35 && throttle > 0.8 && traits.skill > 0.5;

  if (!state.onRoad) {
    // Point back at the track and get on with it. Keep the throttle firmly on
    // and the brake off: mixing the two at low speed on a loose surface just
    // makes the car sit and shuffle instead of driving back onto the road.
    const back = Math.atan2(base.y - state.y, base.x - state.x);
    const relBack = (((back - state.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
    steer = clamp(relBack / 0.5, -1, 1);
    throttle = Math.max(throttle, 0.6);
    brake = 0;
  }

  out.throttle = clamp(throttle, 0, 1);
  out.brake = clamp(brake, 0, 1);
  out.steer = clamp(steer, -1, 1);
  out.handbrake = handbrake;
  out.boost = boost;
}

/** Safe corner speed (px/s) for a given radius under the available grip. */
export function cornerBudget(radius: number, mu: number, scale: number): number {
  if (!Number.isFinite(radius) || radius > 6000) return 100000;
  const v = Math.sqrt(mu * CAR_GRAVITY * Math.max(12, radius)) * scale;
  return v;
}

/** Convenience helper for the client: the surface a car's body sits on. */
export function surfaceLabel(id: string): string {
  const def = SURFACES[id as keyof typeof SURFACES];
  return def ? def.name : 'Off Track';
}
